import { contrast } from './validate.ts';
import type { ColorToken, Theme } from './types.ts';

const TOKEN_VARS: Record<ColorToken, string> = {
  surface: '--surface-0', 'surface-raised': '--surface-1', ink: '--ink', 'ink-muted': '--ink-muted',
  accent: '--accent', danger: '--danger', selected: '--selected', focus: '--focus',
};

const FONTS: Record<NonNullable<Theme['font']>, string> = {
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  rounded: 'ui-rounded, "SF Pro Rounded", "Segoe UI", Nunito, system-ui, sans-serif',
};

function block(colors: Partial<Record<ColorToken, string>> | undefined): string {
  if (!colors) return '';
  // A scheme whose text would be hard to read is ignored, and the client palette stays.
  if (colors.ink && colors.surface && contrast(colors.ink, colors.surface) < 4.5) return '';
  return Object.entries(colors)
    .filter(([k]) => k in TOKEN_VARS)
    .map(([k, v]) => `${TOKEN_VARS[k as ColorToken]}: ${v};`).join(' ');
}

/** CSS that applies a presentation theme over the client's own tokens. */
export function themeCss(theme: Theme, seats: string[]): string {
  const light: string[] = [block(theme.colors)];
  if (theme.font) light.push(`--font-sans: ${FONTS[theme.font]};`);
  if (theme.cardSize) light.push(`--card-width: ${Math.round(theme.cardSize)}px;`);
  seats.forEach((seat, i) => { const c = theme.seatColors?.[seat]; if (c) light.push(`--seat-${i % 8}: ${c};`); });
  const dark = block(theme.colors?.dark);
  let css = `:root { ${light.join(' ')} }`;
  if (dark) css += ` @media (prefers-color-scheme: dark) { :root { ${dark} } }`;
  return css;
}
